/**
 * Shopify Subscription Customization
 * Handles UI customization for the Shopify Subscriptions app block
 * on product pages (one-time purchase vs subscribe & save).
 */
const ShopifySubscription = {
  init() {
    const planCards = document.querySelectorAll(".plan_card");
    if (!planCards.length) return;

    const addToCartButtonText = document.querySelector(
      ".product-form__submit strong"
    );

    planCards.forEach((card) => {
      const oneTimeInput = card.querySelector(
        'input[data-radio-type="one_time_purchase"]'
      );
      const oneTimeWrapper = card.querySelector(
        ".group_list_item.one_time_purchase_option_app_block"
      );
      const subscribeInput = card.querySelector(
        'input[data-radio-type="selling_plan_group"]'
      );
      const subscribeWrapper = card.querySelector(
        ".group_name_with_radio_button"
      );
      const dropdown = card.querySelector(".group_dropdown");

      // Rename "One-time purchase" text
      this.renameOneTimePurchaseText(oneTimeWrapper);

      // Populate inline prices from data attributes
      this.populatePrices(card, oneTimeInput, subscribeInput);

      // Default to one-time purchase
      if (oneTimeInput) {
        oneTimeInput.checked = true;
        oneTimeInput.dispatchEvent(new Event("change", { bubbles: true }));
      }
      if (subscribeInput) subscribeInput.checked = false;
      if (dropdown) dropdown.style.display = "none";

      // Set initial button price based on default checked option
      this.setInitialPrice(oneTimeInput, subscribeInput, dropdown, addToCartButtonText);

      // Set up click handlers
      this.setupOneTimeClickHandler({
        oneTimeWrapper,
        oneTimeInput,
        subscribeInput,
        dropdown,
        addToCartButtonText,
        card,
      });

      this.setupSubscribeClickHandler({
        subscribeWrapper,
        subscribeInput,
        oneTimeInput,
        dropdown,
        addToCartButtonText,
        card,
      });

      // Set up native dropdown change handler
      this.setupDropdownChangeHandler({
        dropdown,
        addToCartButtonText,
        card,
      });

    });
  },

  /**
   * Strips currency code from price string
   * e.g. "$45.00 NZD" -> "$45.00"
   */
  formatPrice(priceText) {
    if (!priceText) return "";
    const trimmed = priceText.trim();
    const spaceIndex = trimmed.indexOf(" ");
    return spaceIndex > 0 ? trimmed.substring(0, spaceIndex) : trimmed;
  },

  /**
   * Renames "One-time purchase" to "Make a one-time purchase"
   */
  renameOneTimePurchaseText(wrapper) {
    if (!wrapper) return;
    const span = wrapper.querySelector("span");
    if (span && span.textContent.trim() === "One-time purchase") {
      span.textContent = "Make a one-time purchase";
    }
  },

  /**
   * Populates the inline price elements from input data attributes.
   * - One-time: shows just the price (e.g. "$45.00")
   * - Subscribe: shows compare-at price strikethrough + sale price (e.g. "$45.00 $38.25")
   */
  populatePrices(card, oneTimeInput, subscribeInput) {
    // One-time price element (first .in_widget_price in the one-time subscription_group)
    const oneTimeGroup = oneTimeInput?.closest(".subscription_group");
    const oneTimePriceEl = oneTimeGroup?.querySelector(".in_widget_price");
    if (oneTimePriceEl && oneTimeInput) {
      const price = this.formatPrice(oneTimeInput.dataset.variantPrice);
      oneTimePriceEl.textContent = price;
      oneTimePriceEl.classList.remove("shopify_subscriptions_app_block--hidden");
      oneTimePriceEl.removeAttribute("aria-hidden");
    }

    // Subscribe price element (in the subscribe subscription_group)
    const subscribeGroup = subscribeInput?.closest(".subscription_group");
    const subscribePriceEl = subscribeGroup?.querySelector(".in_widget_price");
    if (subscribePriceEl && subscribeInput) {
      const compareAtPrice = this.formatPrice(subscribeInput.dataset.variantCompareAtPrice);
      const salePrice = this.formatPrice(subscribeInput.dataset.variantPrice);

      subscribePriceEl.innerHTML = "";
      if (compareAtPrice && compareAtPrice !== salePrice) {
        const strikeEl = document.createElement("s");
        strikeEl.textContent = compareAtPrice;
        subscribePriceEl.appendChild(strikeEl);
        subscribePriceEl.appendChild(document.createTextNode(" "));
      }
      const salePriceEl = document.createElement("span");
      salePriceEl.textContent = salePrice;
      subscribePriceEl.appendChild(salePriceEl);

      subscribePriceEl.classList.remove("shopify_subscriptions_app_block--hidden");
      subscribePriceEl.removeAttribute("aria-hidden");
    }
  },

  /**
   * Updates the subscribe inline price element with the current selection's prices.
   * Called when radio or dropdown changes to prevent Shopify's app from overwriting.
   */
  updateSubscribeInlinePrice(card, price, compareAtPrice) {
    const subscribeGroup = card.querySelector('input[data-radio-type="selling_plan_group"]')?.closest(".subscription_group");
    const subscribePriceEl = subscribeGroup?.querySelector(".in_widget_price");
    if (!subscribePriceEl) return;

    const formattedPrice = this.formatPrice(price);
    const formattedCompare = this.formatPrice(compareAtPrice);

    subscribePriceEl.innerHTML = "";
    if (formattedCompare && formattedCompare !== formattedPrice) {
      const strikeEl = document.createElement("s");
      strikeEl.textContent = formattedCompare;
      subscribePriceEl.appendChild(strikeEl);
      subscribePriceEl.appendChild(document.createTextNode(" "));
    }
    const salePriceEl = document.createElement("span");
    salePriceEl.textContent = formattedPrice;
    subscribePriceEl.appendChild(salePriceEl);

    subscribePriceEl.classList.remove("shopify_subscriptions_app_block--hidden");
    subscribePriceEl.removeAttribute("aria-hidden");
  },

  /**
   * Sets the initial button price based on whichever radio is checked by default
   */
  setInitialPrice(oneTimeInput, subscribeInput, dropdown, addToCartButtonText) {
    if (subscribeInput?.checked) {
      let price = subscribeInput.dataset.variantPrice;
      let compareAtPrice = subscribeInput.dataset.variantCompareAtPrice || "";
      if (dropdown && dropdown.options.length > 0) {
        const selectedOption = dropdown.options[dropdown.selectedIndex];
        price = selectedOption?.dataset.variantPrice || price;
        compareAtPrice = selectedOption?.dataset.variantCompareAtPrice || compareAtPrice;
      }
      const sellingPlanId = this.getSelectedSellingPlanId(dropdown, subscribeInput);
      this.updateSellingPlanInput(sellingPlanId);
      this.updateButtonText(addToCartButtonText, price);
      this.updateProductPrice("subscribe", price, compareAtPrice);
      this.updateVariantPriceInput(price);
    } else if (oneTimeInput?.checked) {
      this.updateSellingPlanInput("");
      this.updateButtonText(addToCartButtonText, oneTimeInput.dataset.variantPrice);
      this.updateProductPrice("one_time", oneTimeInput.dataset.variantPrice, "");
      this.updateVariantPriceInput(oneTimeInput.dataset.variantPrice);
    }
  },

  /**
   * Updates the add-to-cart button text with the given price string
   */
  updateButtonText(buttonTextEl, priceText) {
    if (buttonTextEl) {
      buttonTextEl.textContent = this.formatPrice(priceText);
    }
  },

  /**
   * Updates the product-level price display (.product__price-container)
   * @param {string} mode - "one_time" or "subscribe"
   * @param {string} price - the current price (e.g. "$38.25 NZD")
   * @param {string} compareAtPrice - the compare-at/original price (e.g. "$45.00 NZD")
   */
  updateProductPrice(mode, price, compareAtPrice) {
    const priceContainer = document.querySelector(".product__price-container");
    if (!priceContainer) return;

    const priceEl =
      priceContainer.querySelector(".price.price--show-badge") ||
      priceContainer.querySelector(".price");
    const regularDiv = priceContainer.querySelector(".price__regular");
    const saleDiv = priceContainer.querySelector(".price__sale");
    const regularPriceEl = priceContainer.querySelector(".price__regular .price-item--regular");
    const salePriceEl = priceContainer.querySelector(".price__sale .price-item--sale");
    const comparePriceEl = priceContainer.querySelector(".price__sale s.price-item--regular");
    const subscriptionBadge = priceContainer.querySelector(".price__badge--subscription");
    const saleBadge = priceContainer.querySelector(".price__badge-sale:not(.price__badge--subscription)");

    if (mode === "one_time") {
      // Show regular price, hide sale price
      if (priceEl) priceEl.classList.remove("price--on-sale");
      if (regularDiv) { regularDiv.classList.remove("hidden"); regularDiv.style.display = "block"; }
      if (saleDiv) { saleDiv.classList.add("hidden"); saleDiv.style.display = ""; }
      if (regularPriceEl) regularPriceEl.textContent = this.formatPrice(price);
      if (subscriptionBadge) subscriptionBadge.style.display = "none";
      if (saleBadge) saleBadge.style.display = "none";
    } else {
      // Show sale styling with compare-at price
      if (priceEl) priceEl.classList.add("price--on-sale");
      if (regularDiv) { regularDiv.classList.add("hidden"); regularDiv.style.display = ""; }
      if (saleDiv) { saleDiv.classList.remove("hidden"); saleDiv.style.display = "block"; }
      if (salePriceEl) salePriceEl.textContent = this.formatPrice(price);
      if (comparePriceEl) comparePriceEl.textContent = this.formatPrice(compareAtPrice);
      if (subscriptionBadge) subscriptionBadge.style.display = "none";
      if (saleBadge) saleBadge.style.display = "none";
    }
  },

  /**
   * Handles clicking the "One-time purchase" radio option
   */
  setupOneTimeClickHandler({
    oneTimeWrapper,
    oneTimeInput,
    subscribeInput,
    dropdown,
    addToCartButtonText,
  }) {
    if (!oneTimeWrapper) return;

    oneTimeWrapper.addEventListener("click", () => {
      const price = oneTimeInput?.dataset.variantPrice || "";

      // Update button text, product-level price, and variant price input
      this.updateButtonText(addToCartButtonText, price);
      this.updateProductPrice("one_time", price, "");
      this.updateVariantPriceInput(price);

      // Ensure one-time is checked, subscribe is unchecked
      if (oneTimeInput) {
        oneTimeInput.checked = true;
        oneTimeInput.dispatchEvent(new Event("change", { bubbles: true }));
      }
      if (subscribeInput) subscribeInput.checked = false;

      // Clear the selling_plan so the form submits a one-time purchase
      this.updateSellingPlanInput("");

      // Hide the delivery frequency dropdown
      if (dropdown) dropdown.style.display = "none";
    });
  },

  /**
   * Handles clicking the "Subscribe and save" radio option
   */
  setupSubscribeClickHandler({
    subscribeWrapper,
    subscribeInput,
    oneTimeInput,
    dropdown,
    addToCartButtonText,
  }) {
    if (!subscribeWrapper) return;

    subscribeWrapper.addEventListener("click", () => {
      // Ensure subscribe is checked, one-time is unchecked
      if (subscribeInput) {
        subscribeInput.checked = true;
        subscribeInput.dispatchEvent(new Event("change", { bubbles: true }));
      }
      if (oneTimeInput) oneTimeInput.checked = false;

      // Show the delivery frequency dropdown
      if (dropdown) dropdown.style.display = "";

      // Get price from selected dropdown option or subscribe input
      let price = subscribeInput?.dataset.variantPrice || "";
      let compareAtPrice = subscribeInput?.dataset.variantCompareAtPrice || "";
      if (dropdown && dropdown.options.length > 0) {
        const selectedOption = dropdown.options[dropdown.selectedIndex];
        price = selectedOption?.dataset.variantPrice || price;
        compareAtPrice = selectedOption?.dataset.variantCompareAtPrice || compareAtPrice;
      }

      // Set the selling_plan to the selected subscription plan
      const sellingPlanId = this.getSelectedSellingPlanId(dropdown, subscribeInput);
      this.updateSellingPlanInput(sellingPlanId);

      // Update button text, product-level price, variant price input, and inline widget price
      this.updateButtonText(addToCartButtonText, price);
      this.updateProductPrice("subscribe", price, compareAtPrice);
      this.updateVariantPriceInput(price);
      this.updateSubscribeInlinePrice(card, price, compareAtPrice);
    });
  },

  /**
   * Handles changes to the delivery frequency dropdown
   */
  setupDropdownChangeHandler({ dropdown, addToCartButtonText, card }) {
    if (!dropdown) return;

    dropdown.addEventListener("change", () => {
      const selectedOption = dropdown.options[dropdown.selectedIndex];
      if (selectedOption) {
        const price = selectedOption.dataset.variantPrice || "";
        const compareAtPrice = selectedOption.dataset.variantCompareAtPrice || "";
        this.updateSellingPlanInput(selectedOption.value);
        this.updateButtonText(addToCartButtonText, price);
        this.updateProductPrice("subscribe", price, compareAtPrice);
        this.updateVariantPriceInput(price);
        this.updateSubscribeInlinePrice(card, price, compareAtPrice);
      }
    });
  },

  /**
   * Watches for quantity changes and re-applies the correct subscription price
   * after the theme's JS overwrites the button with the regular price.
   */
  /**
   * Updates the hidden [name=variant_price] input that the theme's
   * QuantityInput.updateProductPrice() reads from when calculating
   * price * quantity for the add-to-cart button.
   */
  /**
   * Returns the selling plan ID from the selected dropdown option,
   * or falls back to the subscribe input's value.
   */
  getSelectedSellingPlanId(dropdown, subscribeInput) {
    if (dropdown && dropdown.options.length > 0) {
      return dropdown.options[dropdown.selectedIndex]?.value || "";
    }
    return subscribeInput?.value || "";
  },

  /**
   * Updates the hidden selling_plan input in the product form.
   * Empty string = one-time purchase, plan ID = subscription.
   */
  updateSellingPlanInput(sellingPlanId) {
    const form = document.querySelector("form[action*='/cart/add']") ||
      document.querySelector("product-form form");
    if (!form) return;

    let input = form.querySelector("input[name='selling_plan']");
    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = "selling_plan";
      form.appendChild(input);
    }
    input.value = sellingPlanId;
  },

  updateVariantPriceInput(priceText) {
    const variantPriceInput = document.querySelector("[name=variant_price]");
    if (!variantPriceInput) return;
    // The hidden input stores the price as a formatted string like "$38.25"
    // Douglas.formatPrice expects cents, so we store just the formatted price
    variantPriceInput.value = this.formatPrice(priceText);
  },
};

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => ShopifySubscription.init());
} else {
  ShopifySubscription.init();
}
